import { Message } from '@flos/react-ui';
import type { LocalDateDTO } from 'gw-edge-client/dist/types/dto/pc/edge/time';
import type { ReactElement, ReactNode } from 'react';
import { TRA_CANCELLATION_COV, TRA_SKIING_COV } from '../../../constants/coverage';
import { convertLocalDateDTOToDate, convertLocalDateToString } from '../../../utils/dateUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';

interface HighlightNoteContent {
    noteTitle?: string;
    noteText: ReactElement;
    noteDescription?: ReactNode;
    blueTheme?: boolean;
}
const getAddCoverageHighlightNote = (
    coverageId: string,
    coverageSubtitle: string,
    flavourName: string,
    renewalDate: string = '',
    getHippoText: (resourceKey: string) => string = () => ''
): HighlightNoteContent | undefined => {
    switch (coverageId) {
        case 'PRCGlassSanitationCov':
            return {
                noteTitle: 'Vær opmærksom på:',
                noteText: (
                    <>
                        <p>
                            {getHippoText(
                                'DSS-ADD-SIMPLE-COVERAGE-CANCELLATION-COVERAGE-INFORMATION-GLASS-SANITATION'
                            )}{' '}
                        </p>
                    </>
                ),
                noteDescription: <span>{coverageSubtitle}</span>
            };
        case 'PRCExtendedElectronicCov':
            if (flavourName === 'top_prc_content') return undefined;
            return {
                noteText: (
                    // TODO: Use hippo label when label id is define in document [Hippo PRC]
                    <span>
                        Det er et krav, at du også har dækningen <b>Kortslutning</b>, når du har{' '}
                        <b>Udvidet elektronik</b>.
                    </span>
                ),
                noteDescription: (
                    // TODO: Use hippo label when label id is define in document [Hippo PRC]
                    <div>
                        <p>{coverageSubtitle}</p>
                        <p>
                            Spar dig selv for individuelle elektronikforsikringer. Vores
                            elektronikforsikring sikrer alle dine indendørs elektriske apparater i
                            en række tilfælde, som ikke er dækket af basisdækningen - fx hvis du
                            taber kaffe i den bærbare, eller dit TV pludselig ikke vil tænde.
                        </p>
                        <span>
                            Vi erstatter op til nyværdi, indtil et apparat er fire år gammelt. Der
                            er en selvrisiko på 1.100 kr.
                        </span>
                    </div>
                )
            };
        case 'TRATopCancellationCov':
            return {
                noteTitle: getHippoText(
                    'DSS-ADD-SIMPLE-COVERAGE-CANCELLATION-COVERAGE-INFORMATION-TITLE'
                ),
                noteText: (
                    <p>
                        {getHippoText('DSS-ADD-SIMPLE-COVERAGE-CANCELLATION-COVERAGE-INFORMATION')}
                    </p>
                ),
                blueTheme: true
            };
        case 'TravelTypeHousehold':
            return {
                // TODO: Use hippo label when label id is define in document [Hippo TRA]
                noteTitle: 'Hvornår gælder ændringen fra?',
                noteText: (
                    // TODO: Use hippo label when label id is define in document [Hippo TRA]
                    <p>Ændringens ikrafttrædelsesdato er {renewalDate}</p>
                ),
                blueTheme: true
            };
        case 'AreaDestinationWorldwide':
            return {
                // TODO: Use hippo label when label id is define in document [Hippo TRA]
                noteTitle: 'Hvornår gælder ændringen fra?',
                noteText: (
                    // TODO: Use hippo label when label id is define in document [Hippo TRA]
                    <p>Din ændring gælder fra i dag.</p>
                ),
                blueTheme: true
            };
        case 'AreaDestinationOnlyEurope':
            return {
                // TODO: Use hippo label when label id is define in document [Hippo TRA]
                noteTitle: 'Hvornår gælder ændringen fra?',
                noteText: (
                    // TODO: Use hippo label when label id is define in document [Hippo TRA]
                    <p>Ændringen til Europa (inkl. Tyrkiet) gælder først fra {renewalDate}.</p>
                ),
                blueTheme: true
            };
        default:
            return undefined;
    }
};

const getRemoveCoverageHighlightNote = (
    coverageId: string,
    coverageName: string,
    renewalDate: string,
    isFromDAFlow: boolean
): HighlightNoteContent | undefined => {
    switch (coverageId) {
        case 'PETLifeCov':
        case 'PETHealthCov':
        case 'PETHealthPlusCov':
            return {
                noteText: (
                    // TODO: Use hippo label when label id is define in document [Hippo Pet]
                    <span>
                        Vær opmærksom på, at hvis du fjerner denne dækning, er det ikke sikkert, at
                        vi kan tilbyde dig den igen.
                    </span>
                )
            };
        case TRA_CANCELLATION_COV:
        case TRA_SKIING_COV:
            if (isFromDAFlow) {
                return undefined;
            } else {
                return {
                    noteTitle: 'Hvornår stopper dækningen?',
                    noteText: <span>{`${coverageName} stopper ${renewalDate}`}</span>,
                    blueTheme: true
                };
            }
        default:
            return undefined;
    }
};

const HighlightInfoForCoverage = ({
    coverageId,
    shouldHide = false,
    editCoverageType,
    renewalDate,
    coverageName = '',
    coverageSubtitle = '',
    flavourName = ''
}: {
    coverageId: string;
    shouldHide?: boolean;
    editCoverageType: 'add' | 'remove';
    renewalDate?: LocalDateDTO;
    coverageName?: string;
    coverageSubtitle?: string;
    flavourName?: string;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const policyRenewalDate = convertLocalDateToString(
        convertLocalDateDTOToDate(renewalDate ?? {})
    );
    const { isFromDAFlow } = useRecoilValue(opcInfoAtom);

    const getHighlightNoteForModal = (
        coverageId: string,
        _renewalDate: string,
        _coverageName: string
    ): HighlightNoteContent | undefined => {
        if (editCoverageType === 'add')
            return getAddCoverageHighlightNote(
                coverageId,
                coverageSubtitle,
                flavourName,
                _renewalDate,
                getHippoText
            );
        if (editCoverageType === 'remove')
            return getRemoveCoverageHighlightNote(
                coverageId,
                _coverageName,
                _renewalDate,
                isFromDAFlow ?? false
            );
    };

    const highlightNote = getHighlightNoteForModal(
        coverageId,
        policyRenewalDate ?? '',
        coverageName
    );

    if (!highlightNote || shouldHide) return <></>;

    return (
        <div className="u-display-flex u-gap-7 u-flex-column u-flex-items-stretch">
            <Message
                theme={highlightNote?.blueTheme ? undefined : 'warning'}
                title={<b>{highlightNote?.noteTitle ?? ''}</b>}
            >
                {highlightNote?.noteText ?? ''}
            </Message>
            {highlightNote?.noteDescription}
        </div>
    );
};
export { getAddCoverageHighlightNote, getRemoveCoverageHighlightNote };
export default HighlightInfoForCoverage;
